<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Usuários" :on-add="() => $router.push({ name: 'create-admins' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
          :vertical="smAndDown"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.roles`]="{ item }">
            <div v-if="item.roles" class="d-flex justify-end justify-md-start ga-1">
              <template v-for="(role, index) in item.roles.slice(0, 3)" :key="index">
                <v-chip
                  v-if="index < 2"
                  :text="role.title"
                  variant="outlined"
                  size="small"
                />
                <v-chip
                  v-else
                  v-tooltip="item.roles.slice(2).map(r => r.title).join(', ')"
                  :text="`+${ item.roles.length - 2 }`"
                  variant="outlined"
                  size="small"
                />
              </template>
            </div>
          </template>
          <template #[`item.active`]="{ item }">
            <span> {{ item.active ? 'Ativo' : 'Inativo' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import { ref, Ref, useTemplateRef } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import type { DataTableFilter, DataTableHeader } from '../../types/DataTable'
import AdminService from '@/services/AdminService'
import type Admin from '@/types/Admin'
import { Resources } from '@/plugins/permissions/typing'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useProducerFilter } from '@/helpers/reports/producerFilter'
import DataTable from '@/components/datatable/DataTable.vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/stores/dialog'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { useAbility } from '@casl/vue'
import { useDisplay } from 'vuetify'
import { useRoleFilter } from '@/helpers/reports/roleFilter'

const { can } = useAbility()
const router = useRouter()
const loader = useLoader()
const dialog = useDialog()
const snackbar = useSnackBar()
const { smAndDown } = useDisplay()

const resource: Resources = 'users'

const datatable = useTemplateRef('datatable')

const headers: Ref<DataTableHeader[]> = ref([
  { key: 'id', value: 'id', title: 'ID', sortable: false },
  { key: 'name', value: 'name', title: 'Nome', sortable: false },
  { key: 'email', value: 'email', title: 'E-mail', sortable: false },
  { key: 'login', value: 'login', title: 'Login', sortable: false },
  { key: 'roles', value: 'roles', title: 'Cargos', sortable: false },
  { key: 'active', value: 'active', title: 'Status', sortable: false, align: 'center' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
])

const actions = ref({
  read: {
    callback: (item: Admin) => router.push({ name: 'show-admins', params: { id: item.id } }),
    icon: 'fa-solid fa-magnifying-glass',
    color: 'primary',
  },
  update: {
    callback: (item: Admin) => router.push({ name: 'edit-admins', params: { id: item.id } }),
    condition: () => can('update', 'users'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: Admin) => destroy(item),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
})

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({
    field: 'name',
    label: 'Nome',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
  useTextFilter({
    field: 'email',
    label: 'E-mail',
    icon: 'fa-regular fa-at',
    required: false,
  }),
  useTextFilter({
    field: 'login',
    label: 'Login',
    icon: 'fa fa-eye',
    required: false,
  }),
  useSelectFilter({
    field: 'status',
    label: 'Status',
    icon: 'fa-regular fa-circle',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Ativo', value: '1' },
      { title: 'Inativo', value: '0' },
    ],
    required: false,
  }),
  useProducerFilter({
    label: 'Produtor',
    field: 'producer_id',
    icon: 'fa-brands fa-product-hunt',
  }),
  useRoleFilter({
    field: 'roles',
    label: 'Cargos',
  }),
  useSelectFilter({
    field: 'has_direct_permissions',
    label: 'Permissões Diretas',
    icon: 'fa-regular fa-gavel',
    clearable: true,
    items: [
      { title: 'Possui', value: '1' },
      { title: 'Não possui', value: '0' },
    ],
    required: false,
  }),
])

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await (new AdminService()).index(page, filters, per_page)

    response.data.data.forEach((admin) => {
      admin.roles?.sort((a, b) => a.title.localeCompare(b.title))
    })

    return response
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os usuários',
      'error',
    )

    throw error
  }
}

async function destroy(item: Admin) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o usuário? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()
    await (new AdminService()).destroy(item.id)

    snackbar.show(
      'Usuário administrador excluído com sucesso.',
      'success',
    )

    if (datatable.value !== null) {
      datatable.value.fetch(1)
    }
  } finally {
    loader.decrement()
  }
}
</script>
